/*
 * moda-coping-estimate.js — the By-length estimate, plus the small helpers both v4 surfaces share
 * (the PDP calculator A and the configurator B). Load moda-coping-takeoff.js first; that engine is a
 * byte-identical copy of v3/takeoff.js and is never edited. Nothing here changes its math.
 *
 * Count rule (plan §2): straights = quick().straights + 1 per edge (edges = inside + outside corners),
 * spares added after, as a percentage of that, rounded up. quick() pools the offcuts of the whole length;
 * the settled cut rule gives each edge its own cut piece, so quick() alone can come up short. One more
 * piece per edge bounds that from above: the estimate is never below the drawn count.
 *
 * API — window.ModaCopingEstimate (module.exports under Node, which needs require('./moda-coping-takeoff.js') first
 * or passes the engine as estimate(opts, engine)):
 *
 *   estimate({ lengthIn, outside = 0, inside = 4 + outside, sparePct = 5 }, engine?) →
 *     { status: 'empty' | 'short' | 'long' | 'ok', lengthIn, inside, outside, edges, sparePct,
 *       quick (engine quick() straights), net (quick + edges), spare, straights (net + spare), straightRunIn }
 *     counts are 0 unless status is 'ok'. 'short' = the corners need more length than entered; 'long' = past MAX_LF.
 *   insideFor(outside) → 4 + outside (a closed pool has four more inside corners than outside ones)
 *   price(config, color, { straight, inside, outside }) →
 *     { mode: 'sales' | 'quote', lines: { straight: { qty, id, unitCents, lineCents, ok }, … }, totalCents, missing: [piece…] }
 *     A piece that is needed (qty > 0) and whose variant is missing, unavailable or unpriced (null or 0) puts the
 *     whole order in quote mode; totalCents is null then. Never $0.
 *   price(…, { sealer: true }) adds the factory-applied penetrating sealer (config addons.sealer, the live "Penetrating
 *     Sealer (per sq ft)" product the paver calculator sells): result.sealer = sealer() below, and its line is in
 *     totalCents. Chosen but unpriced or unavailable, it puts the order in quote mode ('sealer' in missing), as a piece does.
 *   sealer(config, counts) → { offered, ok, id, title, unitCents, sqft, byPiece, lineCents }: byPiece[k] = the top face
 *     of the pieces of that kind ordered (spares included, as the paver calculator seals its overage), rounded up to a
 *     whole sq ft; sqft = their sum. One sealer cart line per piece line, each its quantity (the live theme's
 *     mc-cart-addons.js ties one add-on line to one product line per _group). offered = the config has a sealer
 *     variant (the option shows); ok = it can go in the cart (available and priced). Chosen and not ok: quote mode.
 *   AREA_IN2 — the top face of each piece, sq in.: straight 24 × 14 = 336; either corner 24 × 24 less its 10 × 10 = 476
 *   formatMoney(cents, format = '${{amount}}') — Shopify money_format ({{amount}}, {{amount_no_decimals}},
 *     {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}})
 *   ftIn(inches) → "96 ft 6 in" (nearest 1/16 in.: "96 ft 6 1/2 in"; whole feet: "96 ft")
 *   parseFtIn(ft, inch) → inches, or NaN when either field can't be read (blank counts as 0). Each field takes a
 *     number (6, 6.5), a tape-measure fraction (6 1/2, 6-1/2, 1/2, 6½) and a trailing unit mark (32', 6", 6 in).
 *   opening(lengthIn) → "96 ft 6 in at the water's edge";  corners(inside, outside) → "4 inside · 0 outside"
 *   summary(est) → "96 ft 6 in at the water's edge · 4 inside · 0 outside"
 *   readConfig(id = 'moda-coping-config') → the parsed <script type="application/json"> config, or null
 *   state.defaults()  v3's DEFAULTS() plus sealer: false and v: 4 (shape 'rect', the 32 × 16 ft preset, White, 5 %, by length 96 ft / 4 inside)
 *   state.tidy(S)     known colors and shapes only, spares and corners capped as v3 caps them; unknown fields kept.
 *                     A length past the limits stays as entered (estimate() says 'long', the configurator flags the
 *                     edge), so a pool is never quietly shrunk to fit; only nonsense (not a number, or far past any
 *                     pool) falls back to the defaults.
 *   state.encode(S)   base64url(UTF-8 JSON), byte-for-byte what v3's link() writes after "#t="
 *   state.decode(s)   from a full URL, a hash or the bare token → S, or null for a bad link
 *   state.link(base, S) → base (any old hash dropped) + "#t=" + encode(S)
 *   quoteHref(cfg, lines) → the store's Contact page (cfg.urls.contact) with "Pool coping quote request" and the lines
 *     as its message (?coping=, which the contact section writes into its message box). Eugene 2026-10-13: a quote is
 *     asked on the Contact page, not in a form of the coping pages.
 *   PIECES, COLORS, MAX_LF, MAX_CORNERS, MAX_SPARE, MAX_EDGE, MAX_RUNS
 */
(function (root) {
  'use strict';
  const PIECES = ['straight', 'inside', 'outside'];
  // top face per piece, sq in. (the SolidWorks set: straight 24 × 14; both corners a 24 × 24 L with 14 in. legs)
  const AREA_IN2 = { straight: 24 * 14, inside: 24 * 24 - 10 * 10, outside: 24 * 24 - 10 * 10 };
  const COLORS = ['White', 'Gray', 'Platinum', 'Sand', 'Graphite'];
  const SHAPES = ['rect', 'l', 't', 'custom', 'lf'];
  // the same limits as v3: 4000 ft in all, 1000 ft an edge, 64 edges
  const MAX_LF = 48000, MAX_CORNERS = 200, MAX_SPARE = 50, MAX_EDGE = 12000, MAX_RUNS = 64, EPS = 1e-6;
  // past these a value is not a pool at all (a broken link): the defaults come back
  const SANE_LF = 1e7, SANE_EDGE = 4 * MAX_EDGE;

  const engine = e => e || root.CopingTakeoff || (typeof require === 'function' ? (() => { try { return require('./moda-coping-takeoff.js'); } catch (x) { return null; } })() : null);
  const int = (v, lo, hi) => Math.min(hi, Math.max(lo, Math.round(Number(v)) || 0));
  const insideFor = outside => 4 + int(outside, 0, MAX_CORNERS);

  function estimate(o, eng) {
    const E = engine(eng); if (!E) throw new Error('moda-coping-takeoff.js must load first');
    o = o || {};
    const lengthIn = Number(o.lengthIn), outside = int(o.outside, 0, MAX_CORNERS);
    const inside = o.inside == null ? 4 + outside : int(o.inside, 0, MAX_CORNERS);
    const sparePct = Math.min(MAX_SPARE, Math.max(0, Number(o.sparePct) || 0));
    const out = { status: 'empty', lengthIn: Number.isFinite(lengthIn) && lengthIn > 0 ? lengthIn : 0, inside, outside, edges: inside + outside,
      sparePct, quick: 0, net: 0, spare: 0, straights: 0, straightRunIn: null };
    if (!(lengthIn > 0)) return out;
    if (lengthIn > MAX_LF) return { ...out, status: 'long' };
    const q = E.quick(lengthIn / 12, outside, inside, 0); // same call v3's By length makes, spares added here
    if (q.straight_run_in < 0) return { ...out, status: 'short', straightRunIn: q.straight_run_in };
    const net = q.straights + out.edges;
    const spare = net ? Math.ceil(net * sparePct / 100 - EPS) : 0;
    return { ...out, status: 'ok', straightRunIn: q.straight_run_in, quick: q.straights, net, spare, straights: net + spare };
  }

  const okVariant = v => !!(v && v.id != null && v.available !== false && Number.isFinite(Number(v.price)) && v.price !== null && Number(v.price) > 0);
  function sealer(cfg, counts) {
    const v = cfg && cfg.addons && cfg.addons.sealer, offered = !!(v && v.id != null);
    const byPiece = {};
    PIECES.forEach(k => { const in2 = Math.max(0, Math.round(Number((counts || {})[k]) || 0)) * AREA_IN2[k]; byPiece[k] = in2 > 0 ? Math.ceil(in2 / 144 - EPS) : 0; });
    const sqft = PIECES.reduce((t, k) => t + byPiece[k], 0), ok = offered && okVariant(v), unit = ok ? Math.round(Number(v.price)) : null;
    return { offered, ok, id: offered ? v.id : null, title: (v && v.title) || 'Penetrating sealer', unitCents: unit, sqft, byPiece, lineCents: ok ? unit * sqft : null };
  }

  function price(cfg, color, counts, opts) {
    const lines = {}, missing = []; let total = 0;
    PIECES.forEach(k => {
      const qty = Math.max(0, Math.round(Number((counts || {})[k]) || 0));
      const v = cfg && cfg.variants && cfg.variants[k] ? cfg.variants[k][color] : null;
      const ok = okVariant(v);
      const unit = ok ? Math.round(Number(v.price)) : null;
      lines[k] = { qty, id: v && v.id != null ? v.id : null, unitCents: unit, lineCents: ok ? unit * qty : null, ok };
      if (qty > 0) { if (ok) total += unit * qty; else missing.push(k); }
    });
    // the sealer, when chosen and there is something to seal (an option the config doesn't offer is never chosen)
    const sl = opts && opts.sealer ? sealer(cfg, counts) : null;
    const seal = sl && sl.offered && sl.sqft > 0 ? sl : null;
    if (seal) { if (seal.ok) total += seal.lineCents; else missing.push('sealer'); }
    const mode = missing.length ? 'quote' : 'sales';
    return { mode, lines, totalCents: mode === 'sales' ? total : null, missing, sealer: seal };
  }

  function formatMoney(cents, format) {
    const f = typeof format === 'string' && format ? format : '${{amount}}';
    const n = (Number(cents) || 0) / 100;
    const fmt = (dec, th, ds) => { const [i, d] = n.toFixed(dec).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? ds + d : ''); };
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => k === 'amount_no_decimals' ? fmt(0, ',', '.') : k === 'amount_with_comma_separator' ? fmt(2, '.', ',')
      : k === 'amount_no_decimals_with_comma_separator' ? fmt(0, '.', ',') : fmt(2, ',', '.'))
      .replace(/<[^>]*>/g, ''); // some shops wrap the amount in a <span>; this writes text
  }

  const sixteenths = v => { const w = Math.floor(v + 1e-9); let n = Math.round((v - w) * 16), d = 16; while (n && n % 2 === 0) { n /= 2; d /= 2; } return (w || !n ? String(w) : '') + (n ? (w ? ' ' : '') + `${n}/${d}` : ''); };
  function ftIn(inches) {
    const t = Math.round(Math.abs(Number(inches) || 0) * 16) / 16; let ft = Math.floor(t / 12 + 1e-9), i = t - ft * 12;
    if (i >= 12) { ft += 1; i -= 12; }
    return ft ? (i ? `${ft} ft ${sixteenths(i)} in` : `${ft} ft`) : `${sixteenths(i)} in`;
  }
  // A tape reads in fractions: "6 1/2", "6-1/2", "1/2", "6½" and "6 ½" all work, as do "6.5", "32'" and '6"'.
  const GLYPHS = { '½': 1 / 2, '¼': 1 / 4, '¾': 3 / 4, '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8 };
  function readNumber(s) {
    s = String(s == null ? '' : s).trim().toLowerCase().replace(/\s*(?:'|′|"|″|ft\.?|feet|foot|in\.?|inch|inches)$/, '').trim();
    if (!s) return 0;
    let m = s.match(/^(\d+(?:\.\d+)?)?\s*-?\s*([½¼¾⅛⅜⅝⅞])$/);
    if (m) return (m[1] ? parseFloat(m[1]) : 0) + GLYPHS[m[2]];
    m = s.match(/^(?:(\d+)(?:\s+|\s*-\s*))?(\d+)\s*\/\s*(\d+)$/);
    if (m) return +m[3] > 0 ? (m[1] ? +m[1] : 0) + +m[2] / +m[3] : NaN;
    return /^\d*\.?\d+$|^\d+\.$/.test(s) ? parseFloat(s) : NaN;
  }
  function parseFtIn(ft, inch) {
    const f = readNumber(ft), i = readNumber(inch);
    return Number.isNaN(f) || Number.isNaN(i) ? NaN : f * 12 + i;
  }
  const opening = lengthIn => `${ftIn(lengthIn)} at the water's edge`;
  const corners = (inside, outside) => `${inside} inside · ${outside} outside`;
  const summary = est => `${opening(est.lengthIn)} · ${corners(est.inside, est.outside)}`;

  function readConfig(id) {
    if (typeof document === 'undefined') return null;
    const el = document.getElementById(id || 'moda-coping-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // ── the #t= state: v3's S, so a v4 link opens in v3 and the other way round ──
  function defaults() {
    const E = engine();
    return { shape: 'rect', runs: E ? E.rectangle(384, 192) : [{ length_in: 384, turn: 'R' }, { length_in: 192, turn: 'R' }, { length_in: 384, turn: 'R' }, { length_in: 192, turn: 'R' }],
      project: '', color: 'White', allowance: 5, spareO: 0, spareI: 0, lf: { in: 96 * 12, out: 0, inside: 4 }, sealer: false, v: 4 };
  }
  function tidy(s) {
    const d = defaults(); s = s && typeof s === 'object' ? { ...s } : {};
    if (!COLORS.includes(s.color)) s.color = d.color;
    if (!SHAPES.includes(s.shape)) s.shape = d.shape;
    if (s.base != null && !['rect', 'l', 't'].includes(s.base)) delete s.base;
    s.allowance = s.allowance == null ? d.allowance : Math.min(MAX_SPARE, Math.max(0, Number(s.allowance) || 0)); // left out: v3's 5 %
    s.spareI = int(s.spareI, 0, MAX_SPARE); s.spareO = int(s.spareO, 0, MAX_SPARE);
    s.project = ''; // v4 never carries a project name
    s.sealer = s.sealer === true; // v3 ignores it; a link without it is unsealed
    const lf = s.lf && typeof s.lf === 'object' ? s.lf : d.lf, lfIn = Number(lf.in);
    s.lf = { ...lf, in: Number.isFinite(lfIn) && lfIn > 0 && lfIn <= SANE_LF ? lfIn : 0, out: int(lf.out, 0, MAX_CORNERS), inside: int(lf.inside, 0, MAX_CORNERS) };
    const okRun = r => r && Number(r.length_in) > 0 && Number(r.length_in) <= SANE_EDGE && (r.turn === 'R' || r.turn === 'L');
    if (!Array.isArray(s.runs) || s.runs.length < 4 || s.runs.length > MAX_RUNS || !s.runs.every(okRun)) { s.runs = d.runs; if (s.shape !== 'lf') s.shape = 'rect'; }
    else s.runs = s.runs.map(r => { const x = { ...r, length_in: Number(r.length_in) }; if (x.cut != null && x.cut !== 'single' && x.cut !== 'balanced') delete x.cut; return x; });
    if (s.cutStyle != null && s.cutStyle !== 'single' && s.cutStyle !== 'balanced') delete s.cutStyle;
    s.v = 4;
    return s;
  }
  function b64url(str) {
    const bytes = new TextEncoder().encode(str); let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  const encode = S => b64url(JSON.stringify(S));
  function decode(text) {
    const m = String(text == null ? '' : text).match(/(?:^|[#&?])t=([A-Za-z0-9_-]+)/) || String(text).match(/^([A-Za-z0-9_-]+)$/);
    if (!m) return null;
    try {
      const bin = atob(m[1].replace(/-/g, '+').replace(/_/g, '/'));
      const S = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(bin, c => c.charCodeAt(0))));
      return S && typeof S === 'object' && !Array.isArray(S) ? S : null;
    } catch (e) { return null; }
  }
  const link = (base, S) => String(base || '').replace(/#.*$/, '') + '#t=' + encode(S);
  const quoteHref = (cfg, lines) => {
    const base = String(((cfg && cfg.urls) || {}).contact || '/pages/get-in-touch');
    const msg = ['Pool coping quote request'].concat(lines || []).filter(Boolean).join('\n');
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'coping=' + encodeURIComponent(msg);
  };

  const api = { estimate, insideFor, price, sealer, AREA_IN2, formatMoney, ftIn, parseFtIn, opening, corners, summary, readConfig, quoteHref,
    state: { defaults, tidy, encode, decode, link }, PIECES, COLORS, MAX_LF, MAX_CORNERS, MAX_SPARE, MAX_EDGE, MAX_RUNS };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.ModaCopingEstimate = api;
})(typeof window !== 'undefined' ? window : globalThis);
